/**
 * Que permiso exige cada transicion, y cuales puede ejecutar la persona en una unidad concreta.
 *
 * Es el ESPEJO de las maquinas de estado del backend (`fase5/workflow-v2.ts` para la actividad y
 * `fase5/unit-gates.ts` para la unidad). Antes la UI ofrecia todas las transiciones y dejaba que el backend
 * respondiera 403: el jefe veia opciones que siempre fallaban. Ahora solo se ofrece lo que el rol puede hacer.
 * Sigue siendo capa 1 (UX); el backend revalida, y las compuertas (borradores, aprobaciones) las decide el.
 */
import { ACTIVITY_TRANSITIONS, UNIT_TRANSITIONS, type ActivityStatus, type UnitRatStatus } from '@rat/shared';
import { canIn, type AccessResponse } from '../auth/access';

/** Permiso por transicion de ACTIVIDAD, clave `DESDE>HACIA` (workflow-v2.ts, tabla TRANSITIONS). */
export const ACTIVITY_TRANSITION_PERM: Record<string, string> = {
  'BORRADOR>EN_COMPLETADO': 'activity.update',
  'BORRADOR>EN_REVISION': 'activity.submit',
  'EN_COMPLETADO>BORRADOR': 'activity.update',
  'EN_COMPLETADO>EN_REVISION': 'activity.submit',
  'EN_REVISION>APROBADO': 'activity.review',
  'EN_REVISION>OBSERVADO': 'activity.review',
  'OBSERVADO>CORREGIDO': 'activity.update',
  'CORREGIDO>EN_REVISION': 'activity.submit',
  'APROBADO>CERRADO': 'activity.close',
  'APROBADO>OBSERVADO': 'activity.review',
  'CERRADO>EN_REVISION': 'activity.reopen',
};

/** Permiso por transicion de UNIDAD (unit-gates.ts, tabla UNIT_TRANSITIONS): la unidad ENVIA, el revisor DECIDE. */
export const UNIT_TRANSITION_PERM: Record<string, string> = {
  'PENDIENTE>EN_LEVANTAMIENTO': 'unit.status.submit',
  'EN_LEVANTAMIENTO>EN_REVISION': 'unit.status.submit',
  'EN_REVISION>CON_OBSERVACIONES': 'unit.status.review',
  'EN_REVISION>COMPLETADA': 'unit.status.review',
  'CON_OBSERVACIONES>EN_LEVANTAMIENTO': 'unit.status.submit',
  'CON_OBSERVACIONES>EN_REVISION': 'unit.status.submit',
  'COMPLETADA>CERRADA': 'unit.status.review',
  'COMPLETADA>EN_LEVANTAMIENTO': 'unit.status.reopen',
  'CERRADA>EN_LEVANTAMIENTO': 'unit.status.reopen',
};

/** Transiciones de la actividad que la persona puede ejecutar EN la unidad de esa actividad. */
export function allowedActivityTransitions(
  access: AccessResponse | null | undefined,
  from: ActivityStatus,
  unitId: string | null | undefined,
): ActivityStatus[] {
  return (ACTIVITY_TRANSITIONS[from] ?? []).filter((to) => {
    const perm = ACTIVITY_TRANSITION_PERM[`${from}>${to}`];
    return perm ? canIn(access, perm, unitId) : false; // transicion desconocida: falla cerrada
  });
}

/** Transiciones de la unidad que la persona puede ejecutar EN esa unidad. */
export function allowedUnitTransitions(
  access: AccessResponse | null | undefined,
  from: UnitRatStatus,
  unitId: string | null | undefined,
): UnitRatStatus[] {
  return (UNIT_TRANSITIONS[from] ?? []).filter((to) => {
    const perm = UNIT_TRANSITION_PERM[`${from}>${to}`];
    return perm ? canIn(access, perm, unitId) : false;
  });
}
